import React, { useContext, useState } from 'react';
import { formatMoney, formatNumber } from '../../../../utils/utils';
import { momentParseDate } from '../../../../helpers';
import ButtonTableAction from '../../../../components/buttons/ButtonTableAction';
import DefaultDataTable from '../../../../components/default/DefaultDataTable';
import { DispatchProgram, DispatchProgramFilters, DispatchProgramStatus } from '../../../../app/models/DispatchProgram';
import DispatchProgramItemChangeStatus from '../DispatchProgramItemChangeStatus';
import DispatchProgramFilter from './DispatchProgramFilter';
import DispatchProgramRowsChangeStatus from '../DispatchProgramRowsChangeStatus';
import { Link } from 'react-router-dom';
import DispatchProgramChangeStatus from '../DispatchProgramChangeStatus';

interface Props {
    dispatchPrograms: DispatchProgram[];
    totalRows: number;
    callbackSuccess?: () => void;
    edit?: (dispatchProgramId: number, status: string) => void;
    destroy?: (dispatchProgramId: number) => void;
    show?: (dispatchProgramId: number) => void;
    syncDailyCarrier?: (dispatchProgramId: number) => void;
    changeStatus?: boolean;
    filter: DispatchProgramFilters;
    setFilter: (filter: DispatchProgramFilters) => void;
    loading: boolean;
    isShow?: boolean;
    canBeEdited?: boolean;
    repeat?: (quoteId: number) => void;
    downloadExcel: () => void;
    showMassiveLoad?: () => void;
    setShowingModalDocument?: (value: boolean) => void;
    statuses: DispatchProgramStatus[];
}

const DispatchProgramDataTable = ({
    dispatchPrograms,
    edit,
    destroy,
    totalRows = 0,
    filter,
    setFilter,
    loading,
    downloadExcel,
    show,
    repeat,
    statuses,
    callbackSuccess
}: Props) => {
    const columns = [
        {
            name: 'Despacho',
            selector: (row: any) => row?.id,
            sortable: true,
            sortField: 'id'
        },
        {
            name: 'Sector',
            selector: (row: any) => row?.sector_name ?? row?.sector,
            sortable: false,
            cell: (row: any) => (
                row.sector_name ?? (row.sector === 'ALMACENAMIENTO' ? 'Almacenamiento' : 'Transporte')
            )
        },
        {
            name: 'Servicio',
            selector: (row: any) =>
                row?.warehouse_order_item?.service_name ?? row?.transportation_order_item?.service,
            sortable: false,
            cell: (row: any) =>
                row.warehouse_order_item?.service_name ??
                row.transportation_order_item?.service ??
                '-'
        },
        {
            name: 'Pedido',
            selector: (row: any) => row?.transportation_order_id ?? row?.unified_order_id,
            sortable: true,
            sortField: 'transportation_order_id',
            cell: (row: any) => {
                const orderId = row.unified_order_id ?? row.transportation_order_id;
                const isWarehouse = row.sector === 'ALMACENAMIENTO';
                if (orderId) {
                    return (
                        <div>
                            <Link to={`/pedidos/${orderId}/edit/${isWarehouse ? 'warehouse' : 'transport'}`}>
                                #{orderId}
                            </Link>
                            <br />
                            <small>Posición: {row.position ?? 'N/A'}</small>
                        </div>
                    );
                }
                return (
                    <div>
                        <span>Sin pedido</span>
                        <br />
                        <small>Posición: {row.position ?? 'N/A'}</small>
                    </div>
                );
            }
        },
        {
            name: 'Cotización',
            selector: (row: any) => row?.quote_id,
            sortable: true,
            sortField: 'quote_id',
            cell: (row: any) => {
                const position =
                    row.warehouse_order_item?.quote_detail_position ??
                    row.transportation_order_item?.transportation_quote_detail?.position ??
                    row.transportation_quote_detail?.position ??
                    'N/A';

                return row.quote_id ? (
                    <div>
                        <Link to={`/quotes/${row.quote_id}/show`}>
                            #{row.quote_id}
                        </Link>
                        <br />
                        <small>Posición: {position}</small>
                    </div>
                ) : (
                    <div>
                        Sin cotización
                        <br />
                        <small>Posición: {position}</small>
                    </div>
                );
            }
        },
        {
            name: 'Cliente',
            selector: (row: any) => row?.contact_email,
            sortable: true,
            sortField: 'client',
            cell: (row: any) => row.client?.name ?? '-'
        },
        {
            name: 'Origen',
            selector: (row: any) => row?.transportation_order_item?.origin ?? row?.warehouse_order_item?.depot_name,
            sortable: false,
            cell: (row: any) => {
                if (row.sector === 'ALMACENAMIENTO') {
                    return row.warehouse_order_item?.depot_name ?? '-';
                }
                return row.transportation_order_item?.origin ?? '-';
            }
        },
        {
            name: 'Destino',
            selector: (row: any) => row?.transportation_order_item?.destiny,
            sortable: false,
            cell: (row: any) => {
                if (row.sector === 'ALMACENAMIENTO') {
                    return '-';
                }
                return row.transportation_order_item?.destiny ?? '-';
            }
        },
        {
            name: 'Estado',
            selector: (row: any) => row?.status,
            sortable: true,
            sortField: 'status',
            cell: (row: any, index: any, column: any) => {
                return (
                    <DispatchProgramChangeStatus 
                        dispatchProgramId={row.id!} 
                        statuses={row.statuses} 
                        selectedStatus={row.status_selected}
                        callbackSuccess={callbackSuccess}
                    />
                )
            }
        },
        {
            name: 'Cantidad despachada',
            selector: (row: any) => row?.total_guide,
            sortable: false,
            sortField: 'status',
            cell: (row: any, index: any, column: any) => {
                return row.total_guide ? formatNumber(row.total_guide) : null;
            }
        },
        {
            name: 'Acciones',
            selector: (row: any) => row?.id,
            sortable: false,
            cell: (row: any, index: any, column: any) => (
                <div className="nowrap-cell nowrap-cell-no-min">
                    {repeat && (
                        <ButtonTableAction
                            callbackFunction={() => repeat(row.id ?? -1)}
                            classIcon={'mdi mdi-content-copy'}
                            colorIcon={'text-secondary'}
                            errorMessage={'No se puede duplicar este registro.'}
                            title={'Duplicar'}
                        />
                    )}
                    {show && (
                        <ButtonTableAction
                            callbackFunction={() => show(row.id ?? -1)}
                            classIcon={'mdi mdi-eye'}
                            colorIcon={'text-info'}
                            errorMessage={'No se puede mostrar este registro'}
                            title={'Ver'}
                        />
                    )}
                    {/*{show && <ButtonTableShow callbackShow={() => show(row.id ?? -1)} />}*/}
                    {edit && (
                        <ButtonTableAction
                            callbackFunction={() => edit(row.id ?? -1, row.status)}
                            classIcon={'mdi mdi-pencil'}
                            colorIcon={'text-warning'}
                            errorMessage={'No se puede editar este registro.'}
                            title={'Editar'}
                        />
                    )}
                    {destroy && (
                        <ButtonTableAction
                            callbackFunction={() => destroy(row.id ?? -1)}
                            classIcon={'mdi mdi-delete'}
                            colorIcon={'text-danger'}
                            errorMessage={'No se puede eliminar este registro.'}
                            title={'Eliminar'}
                        />
                    )}
                </div>
            )
        }
    ];

    return (
        <>
            <div className="row">
                <div className="col-12 mt-md-0 mt-3">
                    <div className="row mt-3">
                        <div className="col">
                            <DispatchProgramFilter filter={filter} setFilter={setFilter} />
                        </div>
                        <div className="col-auto" style={{ paddingTop: 22 }}>
                            <button className="btn btn-success btn-sm" onClick={downloadExcel}>
                                <i className="fa fa-file-excel" /> Descargar
                            </button>
                        </div>
                    </div>
                </div>
                <div className="col-12 mt-4">
                    <DefaultDataTable
                        columns={columns}
                        data={dispatchPrograms}
                        progressPending={loading}
                        paginationTotalRows={totalRows}
                        filter={filter}
                        setFilter={setFilter}
                    />
                </div>
            </div>
        </>
    );
};

export default DispatchProgramDataTable;
